import type {
    ProductCustomization,
    PurchasableItem,
    Price
} from 'shared/store/ecommerce/order'
import type { ClickAndCollectPromotion, DeliveryFees } from 'shared/types'
import { catalogType, ecommerceType, promotionType, round } from 'shared/utils'
import { priceSelector } from 'shared/store/ecommerce/order'
import type { RootState } from 'shared/store/reducers'
import { menuSize } from '../../const'

const ROUNDER = 5
const { DELIVERY_IN_APP } = ecommerceType
const { XL: MENU_SIZE_XL } = menuSize

const getProductPrice = (product: PurchasableItem): Price => {
    const { customizedIngredients, promotions } = product
    let originalPrice = product.originalPrice || 0

    if (!!customizedIngredients) {
        originalPrice += calculateCustomizationsPrice(customizedIngredients)
    }

    if (product.type === catalogType.MENU && !!product.steps) {
        product.steps.forEach((step: PurchasableItem) => {
            if (!!step.customizedIngredients) {
                originalPrice += calculateCustomizationsPrice(
                    step.customizedIngredients
                )
            }

            if (!!step.menuExtraPriceTTC && product.menuSize !== MENU_SIZE_XL) {
                originalPrice += step.menuExtraPriceTTC
            }
        })
    }
    let finalPrice = originalPrice || 0

    if (promotions && promotions.length > 0) {
        promotions.forEach(promotion => {
            finalPrice = calculateFinalPriceWithPromotion(finalPrice, promotion)

            // When selecting products with extra price in promotion (ex. king fries in mercredeal)
            finalPrice += computeExtraPrice(product)
        })
    }

    originalPrice = round(originalPrice, ROUNDER)
    finalPrice = round(finalPrice, ROUNDER)

    return { originalPrice, finalPrice }
}

const computeExtraPrice = (product: any): number => {
    if (product.type === catalogType.MENU) {
        return product.steps.reduce((acc, step) => {
            return acc + (step.menuExtraPriceTTC || 0)
        }, 0)
    }
    return 0
}

const calculateCustomizationsPrice = (
    recipe: Array<ProductCustomization>
): number => {
    const price = recipe.reduce((acc, ingredient) => {
        const { initialQuantity, price, quantity } = ingredient
        return acc + (price || 0) * Math.max(quantity - initialQuantity, 0)
    }, 0)
    return round(price, ROUNDER)
}

const calculateFinalPriceWithPromotion = (
    originalPrice: number,
    promotion: ClickAndCollectPromotion,
    cartContent?: Array<PurchasableItem>
): number => {
    const { promoType, promoValue } = promotion

    if (!originalPrice || originalPrice === 0) {
        return 0
    }
    if (promoValue !== undefined) {
        switch (promoType) {
            case promotionType.ITEM_VALUE_FIXED:
            case promotionType.ORDER_VALUE_FIXED:
                return round(Math.max(0, promoValue), ROUNDER)
            case promotionType.ITEM_PERCENT_FIXED:
                return round(
                    Math.max(0, (originalPrice * promoValue) / 100),
                    ROUNDER
                )
            case promotionType.ORDER_PERCENT_FIXED:
                if (!cartContent) {
                    break
                }
                return calculateEachDiscountedPriceAndSum(
                    cartContent,
                    promoValue
                )
            case promotionType.ITEM_VALUE_DISCOUNT:
            case promotionType.ORDER_VALUE_DISCOUNT:
                return round(Math.max(0, originalPrice - promoValue), ROUNDER)
            case promotionType.ITEM_PERCENT_DISCOUNT:
                return round(
                    Math.max(0, (originalPrice * (100 - promoValue)) / 100),
                    ROUNDER
                )
            case promotionType.ORDER_PERCENT_DISCOUNT:
                if (!cartContent) {
                    break
                }
                return calculateEachDiscountedPriceAndSum(
                    cartContent,
                    100 - promoValue
                )
            default:
                return originalPrice
        }
    }
    return round(originalPrice, ROUNDER)
}

const calculateEachDiscountedPriceAndSum = (
    cartContent: Array<PurchasableItem>,
    percentageOfPrice: number
) => {
    let totalPrice = 0
    let totalPromotion = 0
    cartContent.forEach(product => {
        const { quantity, finalPrice } = product
        if (!finalPrice) {
            return totalPrice
        }
        totalPrice += finalPrice * quantity
        totalPromotion += finalPrice * quantity * (100 - percentageOfPrice)
    })
    totalPromotion = Math.round(totalPromotion) / 100
    totalPrice -= totalPromotion
    return round(Math.max(0, totalPrice), ROUNDER)
}

const sumProductFinalPrices = (products: Array<PurchasableItem>): number => {
    const total = products.reduce((acc, product) => {
        const { available, quantity, finalPrice } = product
        if (available !== false && finalPrice !== undefined) {
            return acc + quantity * finalPrice
        }
        return acc
    }, 0)

    return round(total || 0, 2)
}

const selectDeliveryFees = (
    cartPrice: number,
    deliveryFees?: Array<DeliveryFees>
) => {
    if (!deliveryFees) {
        return undefined
    } else if (deliveryFees.length === 1) {
        return deliveryFees[0]
    } else {
        const {
            lowestDeliveryFeesRange,
            highestDeliveryFeesRange
            //$FlowFixMe
        } = deliveryFees.reduce((previousFees, currentFees) => ({
            lowestDeliveryFeesRange:
                previousFees.lowerPrice <= currentFees.lowerPrice
                    ? previousFees
                    : currentFees,
            highestDeliveryFeesRange:
                previousFees.higherPrice >= currentFees.higherPrice
                    ? previousFees
                    : currentFees
        }))
        if (
            lowestDeliveryFeesRange &&
            cartPrice < lowestDeliveryFeesRange.lowerPrice
        ) {
            return lowestDeliveryFeesRange
        }
        if (
            highestDeliveryFeesRange &&
            cartPrice > highestDeliveryFeesRange.higherPrice
        ) {
            return highestDeliveryFeesRange
        }
        return deliveryFees.find(
            fees =>
                cartPrice >= fees.lowerPrice && cartPrice <= fees.higherPrice
        )
    }
}

const getOrderPrice = (state: RootState, ecommerceMode: string) => {
    const { finalPrice, finalPriceWithDeliveryFees } = priceSelector(state)
    if (ecommerceMode === DELIVERY_IN_APP) {
        return finalPriceWithDeliveryFees
    }
    return finalPrice
}

export {
    getProductPrice,
    calculateCustomizationsPrice,
    calculateFinalPriceWithPromotion,
    calculateEachDiscountedPriceAndSum,
    sumProductFinalPrices,
    selectDeliveryFees,
    getOrderPrice
}
